/**
 * Inline icon set.
 *
 * Icons are drawn as SVG rather than pulled from a font/CDN so the app has no
 * external asset dependency. `currentColor` lets Tailwind text utilities drive
 * the colour. Every icon is decorative — meaning is always carried by adjacent
 * text — so they are hidden from assistive technology.
 */
export type IconName =
  | 'calendar'
  | 'refresh'
  | 'download'
  | 'search'
  | 'package'
  | 'truck'
  | 'check'
  | 'clock'
  | 'alert'
  | 'x'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'warehouse'
  | 'inbox'
  | 'sun'
  | 'moon'
  | 'logout'
  | 'lock'
  | 'eye'
  | 'eye-off'
  | 'undo';

const PATHS: Record<IconName, JSX.Element> = {
  calendar: (
    <>
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11a8 8 0 1 0-2.3 5.7" />
      <path d="M20 4.5V11h-6.5" />
    </>
  ),
  download: (
    <>
      <path d="M12 3.5v11" />
      <path d="M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4.5 17.5v1.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-1.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.5 15.5 4.5 4.5" />
    </>
  ),
  package: (
    <>
      <path d="M20.5 7.8v8.4a2 2 0 0 1-1 1.7l-6.5 3.7a2 2 0 0 1-2 0L4.5 17.9a2 2 0 0 1-1-1.7V7.8a2 2 0 0 1 1-1.7l6.5-3.7a2 2 0 0 1 2 0l6.5 3.7a2 2 0 0 1 1 1.7Z" />
      <path d="m3.8 7 8.2 4.7L20.2 7M12 21v-9.3" />
    </>
  ),
  truck: (
    <>
      <path d="M2.5 6.5h10v9h-10z" />
      <path d="M12.5 10h4l3 3v2.5h-7z" />
      <circle cx="6.5" cy="18" r="2" />
      <circle cx="16.5" cy="18" r="2" />
    </>
  ),
  check: <path d="m4.5 12.5 5 5 10-11" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.3l3.3 2" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3.8 21 19.5H3L12 3.8Z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  x: <path d="m6 6 12 12M18 6 6 18" />,
  'chevron-left': <path d="m14.5 5-7 7 7 7" />,
  'chevron-right': <path d="m9.5 5 7 7-7 7" />,
  'chevron-down': <path d="m5 9.5 7 7 7-7" />,
  warehouse: (
    <>
      <path d="M2.8 9.6 12 4.2l9.2 5.4v10.4H2.8z" />
      <path d="M7.5 20V13h9v7" />
      <path d="M7.5 16.5h9" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.5 13.5h4l1.5 3h6l1.5-3h4" />
      <path d="M3.5 13.5 6 5h12l2.5 8.5V19a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19z" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />,
  logout: (
    <>
      <path d="M9.5 20.5H5.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h4" />
      <path d="M15.5 16.5 20 12l-4.5-4.5" />
      <path d="M20 12H9.5" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3 3.8" />
      <path d="M6.5 7.7A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.4 0 2.6-.3 3.7-.8" />
      <path d="M10 10a3 3 0 0 0 4 4" />
      <path d="m3.5 3.5 17 17" />
    </>
  ),
  // Arrow curving back — "returned to origin".
  undo: (
    <>
      <path d="M4 9.5h11a5 5 0 0 1 0 10h-6.5" />
      <path d="M8 5.5 4 9.5l4 4" />
    </>
  ),
};

export function Icon({
  name,
  className = 'w-4 h-4',
  strokeWidth = 1.75,
}: {
  name: IconName;
  className?: string;
  strokeWidth?: number;
}): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
